import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowDown, ArrowUp, HelpCircle, Pencil, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface TemplateSummary {
  id: string;
  name: string;
  description: string | null;
  scope: 'SYSTEM' | 'TENANT';
}

interface StageDetail {
  id: string;
  order: number;
  name: string;
  description: string | null;
  helpText: string | null;
  percentageWeight: string;
  isGate: boolean;
  subtasks: Array<{
    id: string;
    order: number;
    name: string;
    required: boolean;
  }>;
}

interface TemplateDetail extends TemplateSummary {
  clonedFrom: string | null;
  stages: StageDetail[];
}

const SCOPE_BADGE: Record<string, string> = {
  SYSTEM: 'bg-blue-900/40 text-blue-200',
  TENANT: 'bg-emerald-900/40 text-emerald-200',
};

export function ProcessTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selected, setSelected] = useState<TemplateDetail | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [switching, setSwitching] = useState(false);

  // Add-stage form
  const [addingStage, setAddingStage] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageWeight, setNewStageWeight] = useState('');
  const [newStageIsGate, setNewStageIsGate] = useState(false);
  const [newStageHelp, setNewStageHelp] = useState('');
  const [creatingStage, setCreatingStage] = useState(false);

  // Inline edit
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editWeight, setEditWeight] = useState('');
  const [editHelp, setEditHelp] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsGate, setEditIsGate] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await apiClient.get<TemplateSummary[]>('/process-templates');
      setTemplates(data);
    } catch {
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function getErrorDetail(err: unknown): string | null {
    if (!isAxiosError(err)) return null;
    return (err.response?.data as { detail?: string } | undefined)?.detail ?? null;
  }

  async function refreshSelected(id: string) {
    const { data } = await apiClient.get<TemplateDetail>(`/process-templates/${id}`);
    setSelected(data);
  }

  async function openTemplate(id: string) {
    try {
      await refreshSelected(id);
      setCloning(false);
      setCloneName('');
      setEditingStageId(null);
    } catch {
      showToast(t('processTemplates.error_load'));
    }
  }

  async function doClone() {
    if (!selected || !cloneName.trim()) return;
    try {
      const { data } = await apiClient.post<TemplateDetail>(
        `/process-templates/${selected.id}/clone`,
        { name: cloneName.trim() },
      );
      setSelected(data);
      setCloning(false);
      setCloneName('');
      showToast(t('processTemplates.cloned', { name: data.name }));
      loadList();
    } catch {
      showToast(t('processTemplates.error_clone'));
    }
  }

  async function doAddStage() {
    if (!selected || !newStageName.trim()) return;
    setCreatingStage(true);
    try {
      const body: Record<string, unknown> = { name: newStageName.trim() };
      if (newStageWeight.trim()) body.percentageWeight = parseFloat(newStageWeight);
      if (newStageIsGate) body.isGate = true;
      if (newStageHelp.trim()) body.helpText = newStageHelp.trim();
      await apiClient.post(`/process-templates/${selected.id}/stages`, body);
      await refreshSelected(selected.id);
      setAddingStage(false);
      setNewStageName('');
      setNewStageWeight('');
      setNewStageIsGate(false);
      setNewStageHelp('');
      showToast(t('newStage.added', { defaultValue: 'Stage added.' }));
    } catch (err) {
      showToast(getErrorDetail(err) ?? t('newStage.error_generic'));
    } finally {
      setCreatingStage(false);
    }
  }

  function startEdit(stage: StageDetail) {
    setEditingStageId(stage.id);
    setEditName(stage.name);
    setEditWeight(stage.percentageWeight);
    setEditHelp(stage.helpText ?? '');
    setEditDescription(stage.description ?? '');
    setEditIsGate(stage.isGate);
  }

  async function saveEdit(e: FormEvent) {
    e.preventDefault();
    if (!selected || !editingStageId) return;
    setSavingEdit(true);
    try {
      const body: Record<string, unknown> = {
        name: editName.trim(),
        percentageWeight: parseFloat(editWeight) || 0,
        isGate: editIsGate,
        helpText: editHelp,
        description: editDescription,
      };
      await apiClient.patch(
        `/process-templates/${selected.id}/stages/${editingStageId}`,
        body,
      );
      await refreshSelected(selected.id);
      setEditingStageId(null);
      showToast(t('processTemplates.stage_saved'));
    } catch (err) {
      showToast(getErrorDetail(err) ?? t('processTemplates.error_save'));
    } finally {
      setSavingEdit(false);
    }
  }

  async function deleteStage(stage: StageDetail) {
    if (!selected) return;
    if (!confirm(t('processTemplates.confirm_delete_stage', { name: stage.name }))) return;
    try {
      await apiClient.delete(
        `/process-templates/${selected.id}/stages/${stage.id}`,
      );
      await refreshSelected(selected.id);
      showToast(t('processTemplates.stage_deleted'));
    } catch (err) {
      const detail = getErrorDetail(err);
      const status = isAxiosError(err) ? err.response?.status : null;
      if (status === 409) {
        showToast(detail ?? t('processTemplates.stage_busy'));
      } else {
        showToast(detail ?? t('processTemplates.error_delete'));
      }
    }
  }

  async function moveStage(stage: StageDetail, direction: 'up' | 'down') {
    if (!selected) return;
    const sorted = [...selected.stages].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.id === stage.id);
    if (idx < 0) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const reordered = [...sorted];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try {
      await apiClient.put(`/process-templates/${selected.id}/stages/reorder`, {
        stageIds: reordered.map((s) => s.id),
      });
      await refreshSelected(selected.id);
    } catch (err) {
      showToast(getErrorDetail(err) ?? t('processTemplates.error_reorder'));
    }
  }

  async function doSwitch() {
    if (!selected) return;
    setSwitching(true);
    try {
      const { data } = await apiClient.post<{
        applied: boolean;
        openOpportunities: number;
        reason: string | null;
      }>('/process-templates/switch', { templateId: selected.id });
      if (data.applied) {
        showToast(t('processTemplates.active_set', { name: selected.name }));
      } else {
        showToast(data.reason ?? t('processTemplates.switch_blocked'));
      }
    } catch {
      showToast(t('processTemplates.error_switch'));
    } finally {
      setSwitching(false);
    }
  }

  const stagesSorted = selected
    ? [...selected.stages].sort((a, b) => a.order - b.order)
    : [];

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      <h1 className="text-2xl font-semibold">{t('processTemplates.title')}</h1>

      <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
        {/* Template list */}
        <ul className="divide-y rounded-lg border bg-card">
          {templates.map((tpl) => (
            <li key={tpl.id}>
              <button
                type="button"
                onClick={() => openTemplate(tpl.id)}
                className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-accent ${
                  selected?.id === tpl.id ? 'bg-accent' : ''
                }`}
              >
                <div>
                  <div className="font-medium">{tpl.name}</div>
                  {tpl.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {tpl.description}
                    </div>
                  )}
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    SCOPE_BADGE[tpl.scope] ?? 'bg-muted text-foreground'
                  }`}
                >
                  {tpl.scope}
                </span>
              </button>
            </li>
          ))}
          {templates.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('common.loading')}
            </li>
          )}
        </ul>

        {/* Detail panel */}
        <div className="rounded-lg border bg-card p-6">
          {!selected && (
            <p className="text-sm text-muted-foreground">
              {t('processTemplates.select_hint')}
            </p>
          )}
          {selected && (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">{selected.name}</h2>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        SCOPE_BADGE[selected.scope] ?? 'bg-muted'
                      }`}
                    >
                      {selected.scope}
                    </span>
                    {selected.scope === 'SYSTEM' && (
                      <span
                        className="rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium text-amber-200"
                        title={t('processTemplates.system_immutable_help')}
                      >
                        {t('processTemplates.read_only')}
                      </span>
                    )}
                  </div>
                  {selected.description && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {selected.description}
                    </p>
                  )}
                  {selected.clonedFrom && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('processTemplates.cloned_from')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {selected.scope === 'SYSTEM' && !cloning && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setCloneName(`${selected.name} (copy)`);
                        setCloning(true);
                      }}
                    >
                      {t('processTemplates.clone')}
                    </Button>
                  )}
                  <Button size="sm" onClick={doSwitch} disabled={switching}>
                    {switching ? t('processTemplates.switching') : t('processTemplates.set_active')}
                  </Button>
                </div>
              </div>

              {cloning && (
                <div className="mt-4 flex items-end gap-2 rounded-md border bg-muted/50 p-3">
                  <div className="flex-1 space-y-1">
                    <label className="text-sm font-medium">
                      {t('processTemplates.clone_name_label')}
                    </label>
                    <Input
                      value={cloneName}
                      onChange={(e) => setCloneName(e.target.value)}
                      placeholder={t('processTemplates.clone_name_placeholder')}
                    />
                  </div>
                  <Button size="sm" onClick={doClone} disabled={!cloneName.trim()}>
                    {t('processTemplates.clone')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCloning(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              )}

              {/* Stages */}
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('processTemplates.stages_count', { n: stagesSorted.length })}
                  </h3>
                  {selected.scope === 'TENANT' && !addingStage && stagesSorted.length < 6 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAddingStage(true)}
                    >
                      {t('newStage.title')}
                    </Button>
                  )}
                </div>

                {addingStage && (
                  <div className="space-y-2 rounded-md border bg-muted/50 p-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium">{t('newStage.name')}</label>
                      <Input
                        autoFocus
                        value={newStageName}
                        onChange={(e) => setNewStageName(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t('newStage.weight')}</label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={newStageWeight}
                          onChange={(e) => setNewStageWeight(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <label className="flex items-end gap-2 text-xs font-medium">
                        <input
                          type="checkbox"
                          checked={newStageIsGate}
                          onChange={(e) => setNewStageIsGate(e.target.checked)}
                          className="h-4 w-4"
                        />
                        {t('newStage.is_gate')}
                      </label>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium">
                        {t('newStage.help_text')}
                      </label>
                      <Input
                        value={newStageHelp}
                        onChange={(e) => setNewStageHelp(e.target.value)}
                        placeholder={t('newStage.help_text_placeholder')}
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setAddingStage(false);
                          setNewStageName('');
                          setNewStageWeight('');
                          setNewStageIsGate(false);
                          setNewStageHelp('');
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={doAddStage}
                        disabled={!newStageName.trim() || creatingStage}
                      >
                        {creatingStage ? t('newStage.creating') : t('newStage.submit')}
                      </Button>
                    </div>
                  </div>
                )}

                {stagesSorted.map((stage, idx) => {
                  const editable = selected.scope === 'TENANT';
                  const isFirst = idx === 0;
                  const isLast = idx === stagesSorted.length - 1;
                  const isEditing = editingStageId === stage.id;
                  return (
                    <div key={stage.id} className="rounded-md border p-4">
                      {isEditing ? (
                        <form onSubmit={saveEdit} className="space-y-2">
                          <div className="grid grid-cols-[1fr_8rem] gap-2">
                            <div className="space-y-1">
                              <label className="text-xs font-medium">
                                {t('newStage.name')}
                              </label>
                              <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                autoFocus
                                required
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">
                                {t('newStage.weight')}
                              </label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={editWeight}
                                onChange={(e) => setEditWeight(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium">
                              {t('newStage.help_text')}
                            </label>
                            <Input
                              value={editHelp}
                              onChange={(e) => setEditHelp(e.target.value)}
                              placeholder={t('newStage.help_text_placeholder')}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium">
                              {t('processTemplates.stage_description')}
                            </label>
                            <textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              rows={2}
                              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                            />
                          </div>
                          <label className="flex items-center gap-2 text-xs font-medium">
                            <input
                              type="checkbox"
                              checked={editIsGate}
                              onChange={(e) => setEditIsGate(e.target.checked)}
                              className="h-4 w-4"
                            />
                            {t('newStage.is_gate')}
                          </label>
                          <div className="flex justify-end gap-2 pt-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingStageId(null)}
                            >
                              {t('common.cancel')}
                            </Button>
                            <Button type="submit" size="sm" disabled={savingEdit}>
                              {savingEdit ? t('common.saving') : t('common.save')}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                                {stage.order}
                              </span>
                              <span className="font-medium">{stage.name}</span>
                              {stage.isGate && (
                                <span className="rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium text-amber-200">
                                  GATE
                                </span>
                              )}
                              {stage.helpText && (
                                <span title={stage.helpText} className="text-muted-foreground">
                                  <HelpCircle className="h-3.5 w-3.5" />
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-muted-foreground">
                                {stage.percentageWeight}%
                              </span>
                              {editable && (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveStage(stage, 'up')}
                                    disabled={isFirst}
                                    className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30"
                                    aria-label={t('processTemplates.move_up')}
                                  >
                                    <ArrowUp className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveStage(stage, 'down')}
                                    disabled={isLast}
                                    className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30"
                                    aria-label={t('processTemplates.move_down')}
                                  >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(stage)}
                                    className="rounded p-1 text-muted-foreground hover:bg-accent"
                                    aria-label={t('common.edit')}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteStage(stage)}
                                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                    aria-label={t('common.delete')}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          {stage.description && (
                            <p className="ml-8 mt-1 text-xs text-muted-foreground">
                              {stage.description}
                            </p>
                          )}
                          {stage.helpText && (
                            <p className="ml-8 mt-1 text-xs italic text-muted-foreground">
                              {stage.helpText}
                            </p>
                          )}
                          {stage.subtasks.length > 0 && (
                            <ul className="ml-8 mt-2 space-y-1">
                              {stage.subtasks.map((s) => (
                                <li key={s.id} className="flex items-center gap-2 text-sm">
                                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                                  <span>{s.name}</span>
                                  {s.required && (
                                    <span className="text-xs font-medium text-red-500">
                                      required
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
